import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ChooseFood from "assets/illustrations/chooseFoodImage.svg";
import ProcessingOrderImage from "assets/illustrations/processingOrderImage.svg";
import OrderStep2Arrow from "assets/OrderStep2Arrow.svg";
import { BlurredBackground } from "components/primitives/BlurredBackground";
import { DrawerSubHeaderText } from "components/primitives/DrawerSubHeaderText";
import { DrawerToggleIndicator } from "components/primitives/DrawerToggleIndicator";
import { HeaderText } from "components/primitives/HeaderText";
import { StyledBottomMessage } from "components/primitives/StyledBottomMessage";
import { SwipeableArea } from "components/primitives/SwipeableArea";
import { useOpenDoorAlarm } from "hooks/useOpenDoorAlarm";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	selectActiveLocalTransaction,
	selectDiscountCodeState,
	selectIsLocalRefill,
	selectOrderDishTypes,
	selectOrderSummary,
	selectTransactionDishes,
} from "slices/transactionSlice";
import { TransactionState } from "types";
import { DRAWER_TOTAL_HEIGHT, ORDER_STEP_3_SMALL_ORDER_LIMIT } from "utils/designTokens";
import { OpenedDoorWarningBox } from "./components/OpenedDoorWarningBox";
import { OrderDishesList } from "./components/OrderDishesList";
import { OrderListDiscountCodeItem } from "./components/OrderListDiscountCodeItem";
import { OrderDishesWrapper, OrderItemBlockWrapper } from "./components/orderStepDrawerStyles";
import { StyledDishTypeList } from "./components/StyledDishTypeList";
import type { BaseOrderStepProps } from "./OrderStep2";

const BODY_TOP_OFFSET = 220;
const ACTION_BAR_CLEARANCE = 120;
const BODY_CONTENT_HEIGHT = DRAWER_TOTAL_HEIGHT - BODY_TOP_OFFSET - ACTION_BAR_CLEARANCE;

const Body = styled(Box)(() => ({
	width: "100%",
	// Capped at the viewport so the list, not the discount code row, shrinks above the action bar.
	maxHeight: `calc(100vh - ${BODY_TOP_OFFSET}px)`,
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	padding: `0 0 ${ACTION_BAR_CLEARANCE}px 0`,
	img: {
		marginTop: 16,
	},
}));

const StyledEmptyBasketColumn = styled(Box)({
	width: "100%",
	height: BODY_CONTENT_HEIGHT,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	gap: 8,
	"& > svg": {
		flex: "1 1 auto",
		minHeight: 0,
		// Let the viewBox aspect ratio derive the width from the flexed height instead of the 640px attribute.
		width: "auto",
	},
});

const TakeYourMealsHeader = () => {
	const { t } = useTranslation();
	return (
		<>
			<HeaderText variant="h3">{t("order.step3.header")}</HeaderText>
			<OrderStep2Arrow />
			<DrawerSubHeaderText>{t("order.step3.subheader")}</DrawerSubHeaderText>
		</>
	);
};

// Only the stock-check header differs for a refill; OrderStep3 resolves the key once and hands it down.
const STOCK_CHECK_HEADER_KEY = "order.step4.header" as const;
const STOCK_CHECK_REFILL_HEADER_KEY = "order.step4.refillHeader" as const;
type StockCheckHeaderKey = typeof STOCK_CHECK_HEADER_KEY | typeof STOCK_CHECK_REFILL_HEADER_KEY;

const StockCheckHeader = ({ headerKey }: { headerKey: StockCheckHeaderKey }) => {
	const { t } = useTranslation();

	return (
		<>
			<HeaderText variant="h3" hasBorder={true}>
				{t(headerKey)}
			</HeaderText>
			<DrawerSubHeaderText>{t("order.step4.subheader")}</DrawerSubHeaderText>
		</>
	);
};

const StockCheckDrawerMessage = ({ headerKey }: { headerKey: StockCheckHeaderKey }) => {
	const { t } = useTranslation();
	return (
		<StyledBottomMessage data-testid="stock-check">
			<Typography variant="promoHeader">{t(headerKey)}...</Typography>
			<CircularProgress sx={{ margin: "auto", color: "#00396c" }} size="75px" thickness={5} />
		</StyledBottomMessage>
	);
};

export const OrderStep3 = ({ open, toggleDrawer }: BaseOrderStepProps) => {
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const transactionState = transaction?.state;
	const isStockCheck = transactionState === TransactionState.StockCheck;
	// Dismissing the warning/alarm when the transaction leaves Active is owned by useOpenDoorAlarm
	// (it stops, rather than restarting the countdown).
	const { resetCountdown, displayOpenedDoorWarning } = useOpenDoorAlarm(transaction?.type, transactionState);
	const isRefill = useAppSelector(selectIsLocalRefill);
	const stockCheckHeaderKey: StockCheckHeaderKey = isRefill ? STOCK_CHECK_REFILL_HEADER_KEY : STOCK_CHECK_HEADER_KEY;

	useEffect(() => {
		if (displayOpenedDoorWarning) {
			toggleDrawer(true);
		}
	}, [displayOpenedDoorWarning, toggleDrawer]);

	return (
		<>
			<SwipeableArea orderstep={3} open={open}>
				{!open && <BlurredBackground orderStep={3} open={open} />}
				<DrawerToggleIndicator open={open} toggleDrawer={toggleDrawer} />
				<Box sx={{ width: "100%", display: "flex", flexDirection: "column", paddingTop: 2 }}>
					{isStockCheck ? <StockCheckHeader headerKey={stockCheckHeaderKey} /> : <TakeYourMealsHeader />}
				</Box>
			</SwipeableArea>
			<Body
				sx={{
					...(open && {
						marginTop: `${BODY_TOP_OFFSET}px`,
					}),
				}}
			>
				<OrderStep3Body
					displayOpenedDoorWarning={displayOpenedDoorWarning}
					resetCountdown={resetCountdown}
					stockCheckHeaderKey={stockCheckHeaderKey}
				/>
			</Body>
		</>
	);
};

interface OrderStep3BodyProps {
	displayOpenedDoorWarning: boolean;
	resetCountdown: () => void;
	stockCheckHeaderKey: StockCheckHeaderKey;
}

const OrderStep3Body = (props: OrderStep3BodyProps) => {
	const orderDishes = useAppSelector(selectTransactionDishes);
	const orderDishTypes = useAppSelector(selectOrderDishTypes);
	const orderSummary = useAppSelector(selectOrderSummary);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const discountCode = useAppSelector(selectDiscountCodeState);
	const transactionState = transaction?.state;
	const { displayOpenedDoorWarning, resetCountdown, stockCheckHeaderKey } = props;

	if (orderDishes.length === 0 && transactionState === TransactionState.Active) {
		return (
			<>
				<StyledEmptyBasketColumn>
					<ChooseFood />
					<OrderListDiscountCodeItem discountCode={discountCode} isOrderFinished={false} />
				</StyledEmptyBasketColumn>
				{displayOpenedDoorWarning && <OpenedDoorWarningBox resetCountdown={resetCountdown} />}
			</>
		);
	}

	if (orderDishes.length > 0) {
		return (
			<>
				<StyledDishTypeList
					// The aggregate count: OrderDishesList renders discounted types alongside full-price ones.
					isLargeOrder={(orderSummary?.orderDishTypesCount ?? 0) > ORDER_STEP_3_SMALL_ORDER_LIMIT}
					data-testid="dish-list"
				>
					<OrderItemBlockWrapper>
						{orderDishTypes && (
							<OrderDishesWrapper>
								<OrderDishesList smallOrderLimit={ORDER_STEP_3_SMALL_ORDER_LIMIT} fitContent={discountCode !== null} />
							</OrderDishesWrapper>
						)}
					</OrderItemBlockWrapper>
				</StyledDishTypeList>
				{/* Outside the masked scroll list, so dishes never scroll under the translucent row.
				    Dropped during the stock check — the message below occupies the same band. */}
				{transactionState === TransactionState.Active && (
					<OrderListDiscountCodeItem discountCode={discountCode} isOrderFinished={false} />
				)}
				{transactionState === TransactionState.StockCheck && (
					<StockCheckDrawerMessage headerKey={stockCheckHeaderKey} />
				)}
				{displayOpenedDoorWarning && <OpenedDoorWarningBox resetCountdown={resetCountdown} />}
			</>
		);
	}

	if (transactionState === TransactionState.StockCheck) {
		return (
			<>
				<ProcessingOrderImage
					style={{
						position: "absolute",
						top: "165px",
					}}
				/>
				<StockCheckDrawerMessage headerKey={stockCheckHeaderKey} />
			</>
		);
	}
};
